import React, {useState} from 'react';
import {Pressable, StyleSheet, View, type StyleProp, type ViewStyle} from 'react-native';

import {formatCount} from '@/shared/lib/format';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme, withAlpha} from '@/shared/ui/theme';

/**
 * Столбчатая диаграмма с накоплением — shadcn `ChartContainer` + `BarChart stacked`,
 * нарисованная из `View`: ни SVG, ни нативного модуля, ни библиотеки графиков.
 *
 * Что она обещает:
 *
 *   - Столбик — сумма серий, каждая серия своим цветом снизу вверх в порядке
 *     объявления. Ненулевая серия никогда не тоньше `MIN_SEGMENT`: «один отказ» на
 *     фоне сотни проходов обязан быть виден, иначе диаграмма врёт о безупречности.
 *   - Ось Y — ноль и максимум, больше делений не надо: график читают ради формы, а
 *     точную цифру дают нажатием на столбик (`readout` под легендой).
 *   - Подписи X — как передали; при большом числе столбиков (24 часа) печатается
 *     каждая `labelEvery`-я, остальные пустые, чтобы не слипались.
 */
export type ChartSeries = {key: string; label: string; color: string};

export type ChartPoint = {
  /** Подпись под столбиком. */
  label: string;
  /** Полная подпись для считывания — «14 сентября», «09:00–10:00». */
  title?: string;
  values: Record<string, number>;
  /** Затенённый столбик: выходной, будущее. */
  muted?: boolean;
};

const MIN_SEGMENT = 3;

const styles = StyleSheet.create({
  root: {gap: spacing(2)},
  head: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing(3), flexWrap: 'wrap'},
  legend: {flexDirection: 'row', alignItems: 'center', gap: spacing(3), flexWrap: 'wrap'},
  legendItem: {flexDirection: 'row', alignItems: 'center', gap: spacing(1) + 2},
  dot: {width: 8, height: 8, borderRadius: 4},
  readout: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    flexWrap: 'wrap',
    minHeight: 28,
    paddingHorizontal: spacing(3),
    borderRadius: radii.md,
  },
  frame: {flexDirection: 'row', gap: spacing(2)},
  axis: {width: 24, justifyContent: 'space-between', alignItems: 'flex-end'},
  plot: {flex: 1, minWidth: 0},
  bars: {flexDirection: 'row', alignItems: 'flex-end'},
  column: {flex: 1, minWidth: 0, alignItems: 'center', justifyContent: 'flex-end', height: '100%'},
  bar: {width: '100%', maxWidth: 36, borderTopLeftRadius: 3, borderTopRightRadius: 3, overflow: 'hidden', justifyContent: 'flex-end'},
  segment: {width: '100%'},
  labels: {flexDirection: 'row', marginTop: spacing(1)},
  /* Подпись шире своей ячейки (24 часа в 300pt — по 12pt на час): ячейка её не
     режет, а центрирует, и печатается только каждая `labelEvery`-я. */
  labelCell: {flex: 1, minWidth: 0, alignItems: 'center', overflow: 'visible'},
  label: {minWidth: 28, textAlign: 'center'},
  baseline: {height: StyleSheet.hairlineWidth},
  barPicked: {maxWidth: 40},
  stub: {height: 2},
  muted: {opacity: 0.45},
});

export function BarChart({
  series,
  points,
  height = 140,
  labelEvery = 1,
  /** Что печатать, пока столбик не нажат. */
  idleText,
  style,
  testID,
}: {
  series: ChartSeries[];
  points: ChartPoint[];
  height?: number;
  labelEvery?: number;
  idleText?: string;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const p = useTheme();
  const [picked, setPicked] = useState<number | null>(null);

  const totals = points.map(pt => series.reduce((sum, s) => sum + (pt.values[s.key] ?? 0), 0));
  const max = Math.max(1, ...totals);
  const current = picked != null ? points[picked] : undefined;
  const gap = points.length > 12 ? 2 : spacing(1);

  return (
    <View style={[styles.root, style]} testID={testID}>
      <View style={styles.head}>
        <View style={[styles.readout, {backgroundColor: p.muted}]}>
          {current ? (
            <>
              <AppText variant="label">{current.title ?? current.label}</AppText>
              {series.map(s => (
                <View key={s.key} style={styles.legendItem}>
                  <View style={[styles.dot, {backgroundColor: s.color}]} />
                  <AppText variant="caption">
                    {s.label}: {formatCount(current.values[s.key] ?? 0)}
                  </AppText>
                </View>
              ))}
            </>
          ) : (
            <AppText variant="caption">{idleText ?? ''}</AppText>
          )}
        </View>
        <View style={styles.legend}>
          {series.map(s => (
            <View key={s.key} style={styles.legendItem}>
              <View style={[styles.dot, {backgroundColor: s.color}]} />
              <AppText variant="caption">{s.label}</AppText>
            </View>
          ))}
        </View>
      </View>

      <View style={[styles.frame, {height}]}>
        <View style={styles.axis}>
          <AppText variant="micro">{formatCount(max)}</AppText>
          <AppText variant="micro">0</AppText>
        </View>
        <View style={styles.plot}>
          <View style={[styles.bars, {height, gap}]}>
            {points.map((pt, i) => {
              const total = totals[i];
              const selected = picked === i;
              // Высота столбика по сумме; каждая ненулевая серия не тоньше минимума.
              const barHeight = Math.max(total > 0 ? MIN_SEGMENT : 0, (total / max) * (height - 4));
              return (
                <Pressable
                  key={`${pt.label}-${i}`}
                  accessibilityRole="button"
                  accessibilityLabel={`${pt.title ?? pt.label}: ${formatCount(total)}`}
                  onPress={() => setPicked(selected ? null : i)}
                  style={styles.column}>
                  <View
                    style={[
                      styles.bar,
                      {height: barHeight},
                      pt.muted && styles.muted,
                      selected && styles.barPicked,
                    ]}>
                    {[...series].reverse().map(s => {
                      const v = pt.values[s.key] ?? 0;
                      if (v <= 0) {
                        return null;
                      }
                      const share = (v / Math.max(total, 1)) * barHeight;
                      return (
                        <View
                          key={s.key}
                          style={[
                            styles.segment,
                            {height: Math.max(MIN_SEGMENT, share), backgroundColor: s.color},
                          ]}
                        />
                      );
                    })}
                    {total === 0 ? (
                      <View
                        style={[
                          styles.segment,
                          styles.stub,
                          {backgroundColor: withAlpha(p.mutedForeground, 0.3)},
                        ]}
                      />
                    ) : null}
                  </View>
                </Pressable>
              );
            })}
          </View>
          <View style={[styles.baseline, {backgroundColor: p.border}]} />
          <View style={[styles.labels, {gap}]}>
            {points.map((pt, i) => (
              <View key={`${pt.label}-${i}`} style={styles.labelCell}>
                <AppText
                  variant="micro"
                  numberOfLines={1}
                  color={picked === i ? p.foreground : undefined}
                  style={styles.label}>
                  {i % labelEvery === 0 ? pt.label : ''}
                </AppText>
              </View>
            ))}
          </View>
        </View>
      </View>
    </View>
  );
}
